import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Copy, Check, Star } from 'lucide-react';
import { toast } from 'sonner';
import { reviewsAPI } from '../services/api';
import { useT } from '../services/i18n';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '');

export const RewardCode = ({ reward, title }) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  if (!reward?.code) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(reward.code); setCopied(true); toast.success(t('reviews.rewardCopied')); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="review-reward-code-box">
      <p className="text-sm font-semibold text-green-800 flex items-center gap-2"><Gift size={16} /> {title || t('reviews.rewardThanks', { percent: reward.percent })}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="text-base font-mono font-bold tracking-wider bg-white border border-green-300 rounded px-3 py-1.5 text-green-900" data-testid="review-reward-code">{reward.code}</code>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-sm text-green-800 hover:underline" data-testid="review-reward-copy">{copied ? <Check size={14} /> : <Copy size={14} />} {t('reviews.rewardCopy')}</button>
      </div>
      <p className="text-xs text-green-700 mt-2">{reward.used ? t('reviews.rewardUsed') : t('reviews.rewardValidity', { date: fmtDate(reward.ends_at) })}{reward.min_order > 0 ? ` · ${t('reviews.rewardMinOrder', { amount: reward.min_order.toFixed(2) })}` : ''}</p>
    </div>
  );
};

/** Product-page banner: « leave a verified review, get -X % on your next order ». */
const ReviewRewardBanner = ({ productId, isAuthenticated, canReview, alreadyReviewed, myReward, onWriteReview }) => {
  const { t } = useT();
  const [reward, setReward] = useState(null);
  useEffect(() => { reviewsAPI.reward().then(setReward).catch(() => setReward(null)); }, []);
  if (!reward?.enabled) return null;
  if (alreadyReviewed && myReward) return <RewardCode reward={myReward} />;
  if (alreadyReviewed) return null;
  return (
    <div className="rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 flex flex-wrap items-center gap-4" data-testid="review-reward-banner">
      <div className="w-11 h-11 rounded-full bg-orange-500 text-white flex items-center justify-center shrink-0"><Gift size={22} /></div>
      <div className="flex-1 min-w-[12rem]">
        <p className="text-sm font-semibold text-gray-900">{t('reviews.rewardTitle', { percent: reward.percent })}</p>
        <p className="text-xs text-gray-600 mt-0.5">{t('reviews.rewardText', { days: reward.validity_days })}</p>
      </div>
      {!isAuthenticated ? (
        <Link to={`/connexion?redirect=/produit/${productId}?avis=1`} className="inline-flex items-center gap-1 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-md px-3 py-2" data-testid="review-reward-login">{t('reviews.rewardLogin')}</Link>
      ) : canReview ? (
        <button type="button" onClick={onWriteReview} className="inline-flex items-center gap-1 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-md px-3 py-2" data-testid="review-reward-write"><Star size={14} /> {t('reviews.rewardWrite')}</button>
      ) : (
        <span className="text-xs text-gray-500" data-testid="review-reward-after-purchase">{t('reviews.rewardAfterPurchase')}</span>
      )}
    </div>
  );
};

export default ReviewRewardBanner;
